import * as THREE from 'three'
import type { ImportedBodyEntry } from './glb'
import { buildImportedTopologySource } from './topology-source/buildImportedTopologySource'
import type { ImportedModelUnitContext, ImportedTopologySource } from './types'

/**
 * 装配局部几何拓扑载荷。
 *
 * 设计说明：
 * 1. `sceneTemplate` 是最终进入场景树的可见模板；
 * 2. `localTopologySource` 是与这份模板一一对应的权威 BRep 拓扑源；
 * 3. 二者成对返回，避免上层只保存可见树却忘记保存对应的交互拓扑资产。
 */
export interface ImportedAssemblyLocalTopologyPayload {
  sceneTemplate: THREE.Object3D
  bodyEntries?: ImportedBodyEntry[]
  localTopologySource: ImportedTopologySource
}

/**
 * 基于装配自身的局部几何模板构建拓扑载荷。
 *
 * 设计说明：
 * 1. 该函数不会额外克隆传入的模板，而是直接在模板上写入 `partAssetLocal/faceTriangleRanges` 等运行时注解；
 * 2. 这样后续 mounted 到场景里的 mesh 与 BRep topology source 会共享同一份 `meshId/primitiveId` 约定；
 * 3. 上层若需要隔离模板生命周期，应在调用前自行完成克隆或裁剪。
 *
 * @param sceneTemplate 装配局部几何模板。
 * @returns 可见模板与拓扑源组成的载荷。
 */
export function createImportedAssemblyLocalTopologyPayload(
  sceneTemplate: THREE.Object3D,
  unitContext?: ImportedModelUnitContext,
): ImportedAssemblyLocalTopologyPayload {
  const localTopologySource = buildImportedTopologySource(sceneTemplate, {
    unitContext,
  })

  return {
    sceneTemplate,
    bodyEntries: localTopologySource.bodyEntries.length > 0
      ? localTopologySource.bodyEntries
      : undefined,
    localTopologySource,
  }
}
